import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { useTranslation } from "react-i18next";
import {
  Shield, Plus, Trash2, Edit, CheckCircle2, XCircle,
  Building2, Globe, Key, Loader2, X, Users, Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const providerLabels = {
  azure_ad: "Microsoft Azure AD",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

const EnterpriseSSOPage = () => {
  const { t } = useTranslation('platform');
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    domain: "", company_name: "", provider: "azure_ad",
    client_id: "", client_secret: "", tenant_id: "common",
    okta_domain: "", default_role: "client",
  });

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/enterprise/sso/configs`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setConfigs(d.configs || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/enterprise/sso/log`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setLogs(d.logs || []); }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchConfigs(); fetchLogs(); }, [fetchConfigs, fetchLogs]);

  const handleSave = async () => {
    const url = editId
      ? `${API}/api/enterprise/sso/configs/${editId}`
      : `${API}/api/enterprise/sso/configs`;
    const method = editId ? "PUT" : "POST";
    await fetch(url, {
      method, credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setShowForm(false);
    setEditId(null);
    setForm({ domain: "", company_name: "", provider: "azure_ad", client_id: "", client_secret: "", tenant_id: "common", okta_domain: "", default_role: "client" });
    fetchConfigs();
  };

  const handleDelete = async (ssoId) => {
    if (!window.confirm(t('platform:sso.deleteConfirm'))) return;
    await fetch(`${API}/api/enterprise/sso/configs/${ssoId}`, { method: "DELETE", credentials: "include" });
    fetchConfigs();
  };

  const handleEdit = (config) => {
    setEditId(config.sso_id);
    setForm({
      domain: config.domain, company_name: config.company_name, provider: config.provider,
      client_id: config.client_id, client_secret: config.client_secret || "",
      tenant_id: config.tenant_id || "common", okta_domain: config.okta_domain || "",
      default_role: config.default_role || "client",
    });
    setShowForm(true);
  };

  return (
    <QMSLayout title={t('platform:sso.title')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="ent-sso-title">
            Enterprise SSO Management
          </h2>
          <p className="text-[#8B95A5] text-sm mt-1">
            Configure domain-based SSO for company-branded access
          </p>
        </div>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm({ domain: "", company_name: "", provider: "azure_ad", client_id: "", client_secret: "", tenant_id: "common", okta_domain: "", default_role: "client" }); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-medium hover:bg-[#D4B85C]"
          data-testid="add-sso-config">
          <Plus size={14} /> Add SSO Configuration
        </button>
      </div>

      {/* Config Cards */}
      {loading ? (
        <div className="text-center py-12 text-[#8B95A5]">
          <Loader2 size={24} className="animate-spin inline mr-2" /> Loading...
        </div>
      ) : configs.length === 0 ? (
        <div className="qms-card">
          <div className="qms-card-body text-center py-12">
            <Shield size={40} className="mx-auto mb-3 text-[#8B95A5] opacity-40" />
            <p className="text-[#8B95A5] text-sm mb-1">No Enterprise SSO configurations yet</p>
            <p className="text-[#555] text-xs">Add a configuration to enable company-branded single sign-on</p>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {configs.map(config => (
            <div key={config.sso_id} className="qms-card" data-testid={`sso-config-${config.sso_id}`}>
              <div className="qms-card-body">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 size={18} className="text-[#C9A84C]" />
                    <h3 className="text-white font-semibold text-sm">{config.company_name}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    {config.active ? (
                      <span className="px-2 py-0.5 rounded bg-[#4ADE8015] text-[#4ADE80] text-[10px]">Active</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-[#EF444415] text-[#EF4444] text-[10px]">Inactive</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-2 text-[#8B95A5]">
                    <Globe size={12} /> <span className="text-[#CCC]">@{config.domain}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#8B95A5]">
                    <Key size={12} /> <span className="text-[#CCC]">{providerLabels[config.provider] || config.provider}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#8B95A5]">
                    <Users size={12} /> <span className="text-[#CCC]">Default role: {config.default_role}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[rgba(255,255,255,0.06)]">
                  <button onClick={() => handleEdit(config)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[#38BDF8] hover:bg-[#38BDF810] text-xs">
                    <Edit size={12} /> Edit
                  </button>
                  <button onClick={() => handleDelete(config.sso_id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[#EF4444] hover:bg-[#EF444410] text-xs">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent SSO Logins */}
      {logs.length > 0 && (
        <div className="mt-6">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Clock size={14} className="text-[#8B95A5]" /> Recent SSO Logins
          </h3>
          <div className="qms-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(255,255,255,0.06)]">
                    <th className="text-left text-[#8B95A5] text-[10px] uppercase p-3">Email</th>
                    <th className="text-left text-[#8B95A5] text-[10px] uppercase p-3">Action</th>
                    <th className="text-left text-[#8B95A5] text-[10px] uppercase p-3">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.slice(0, 10).map((log, i) => (
                    <tr key={i} className="border-b border-[rgba(255,255,255,0.03)]">
                      <td className="p-3 text-[#CCC] text-xs">{log.email}</td>
                      <td className="p-3 text-[#4ADE80] text-xs">{log.action}</td>
                      <td className="p-3 text-[#8B95A5] text-[10px]">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#151820] border border-[rgba(255,255,255,0.08)] rounded-lg w-full max-w-lg" data-testid="sso-form-modal">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.06)]">
              <h3 className="text-white font-semibold text-sm">
                {editId ? t('platform:sso.editConfig') : t('platform:sso.addConfig')}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-[#8B95A5] hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.companyName')}</label>
                <input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
                  placeholder="Acme Corporation"
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="sso-company-name" />
              </div>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.domain')}</label>
                <input value={form.domain} onChange={e => setForm({...form, domain: e.target.value})}
                  placeholder="acme.com"
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="sso-domain" />
              </div>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.identityProvider')}</label>
                <select value={form.provider} onChange={e => setForm({...form, provider: e.target.value})}
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="sso-provider">
                  <option value="azure_ad">Microsoft Azure AD</option>
                  <option value="google_workspace">Google Workspace</option>
                  <option value="okta">Okta</option>
                </select>
              </div>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.clientId')}</label>
                <input value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})}
                  placeholder="Application (client) ID"
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="sso-client-id" />
              </div>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.clientSecret')}</label>
                <input type="password" value={form.client_secret} onChange={e => setForm({...form, client_secret: e.target.value})}
                  placeholder="Client secret value"
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="sso-client-secret" />
              </div>
              {form.provider === "azure_ad" && (
                <div>
                  <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.tenantId')}</label>
                  <input value={form.tenant_id} onChange={e => setForm({...form, tenant_id: e.target.value})}
                    placeholder="common (or your directory tenant ID)"
                    className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                    data-testid="sso-tenant-id" />
                </div>
              )}
              {form.provider === "okta" && (
                <div>
                  <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.oktaDomain')}</label>
                  <input value={form.okta_domain} onChange={e => setForm({...form, okta_domain: e.target.value})}
                    placeholder="your-company.okta.com"
                    className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                    data-testid="sso-okta-domain" />
                </div>
              )}
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">{t('platform:sso.defaultRole')}</label>
                <select value={form.default_role} onChange={e => setForm({...form, default_role: e.target.value})}
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm"
                  data-testid="sso-default-role">
                  <option value="client">{t('platform:sso.roleClient')}</option>
                  <option value="auditor">{t('platform:sso.roleAuditor')}</option>
                  <option value="admin">{t('platform:sso.roleAdmin')}</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-[rgba(255,255,255,0.06)]">
              <button onClick={() => setShowForm(false)}
                className="px-4 py-2 text-[#8B95A5] text-xs hover:text-white">{t('common:actions.cancel')}</button>
              <button onClick={handleSave} disabled={!form.domain || !form.company_name || !form.client_id}
                className="px-4 py-2 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-medium hover:bg-[#D4B85C] disabled:opacity-50"
                data-testid="save-sso-config">
                {editId ? t('platform:sso.updateConfig') : t('platform:sso.createConfig')}
              </button>
            </div>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default EnterpriseSSOPage;
